import { useMemo, useState } from 'react';
import { api, errorMessage, useMeta } from '../api.ts';
import { formatBand, roundBand } from '../../shared/format.ts';
import type { ImportParseResponse, ImportRow, QsoSaveResult } from '../../shared/types.ts';
import { CountrySelect, ErrorBox, Pagination, Spinner, useCountryMap } from './ui.tsx';

const PAGE = 200;
const BATCH = 1000;

/** ADIF-tiedoston valinta ja jäsennys palvelimella */
export function AdifUpload({ onParsed, disabled }: { onParsed: (r: ImportParseResponse, fileName: string) => void; disabled?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [file, setFile] = useState<File | null>(null);

  const parse = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      onParsed(await api.post<ImportParseResponse>('/import/parse', fd), file.name);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <div className="row">
        <input
          type="file"
          accept=".adi,.adif,.txt"
          className="input"
          style={{ maxWidth: 420, paddingTop: 6 }}
          aria-label="ADIF-tiedosto"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          disabled={disabled || busy}
        />
        <button className="btn btn-primary" onClick={parse} disabled={!file || busy || disabled}>
          {busy ? (
            <>
              <Spinner /> Luetaan…
            </>
          ) : (
            'Lue tiedosto'
          )}
        </button>
      </div>
      <ErrorBox error={error} />
    </div>
  );
}

type View = 'all' | 'warnings' | 'unselected';

/**
 * Esikatselu: käyttäjä tarkistaa tunnistetut maat ja valitsee lisättävät rivit.
 * Hyväksytyt rivit lähetetään erissä annettuun osoitteeseen.
 */
export function ImportPreview({
  initial,
  target,
  fileName,
  onDone,
  rowFilter,
}: {
  initial: ImportRow[];
  target: string;
  fileName: string;
  onDone: (result: QsoSaveResult) => void;
  /** Esim. kilpailun aikaväli: rivit, jotka eivät kelpaa, näytetään harmaana eikä niitä voi valita */
  rowFilter?: (r: ImportRow) => string | null;
}) {
  const meta = useMeta();
  const countries = useCountryMap();
  const [rows, setRows] = useState(() =>
    initial.map((r) => {
      const block = rowFilter?.(r) ?? null;
      return block ? { ...r, selected: false, warnings: [block, ...r.warnings] } : r;
    }),
  );
  const [view, setView] = useState<View>(() => (initial.some((r) => r.warnings.length) ? 'warnings' : 'all'));
  const [page, setPage] = useState(1);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const visible = useMemo(
    () => rows.filter((r) => (view === 'warnings' ? r.warnings.length > 0 : view === 'unselected' ? !r.selected : true)),
    [rows, view],
  );
  const pages = Math.max(1, Math.ceil(visible.length / PAGE));
  const shown = visible.slice((page - 1) * PAGE, page * PAGE);
  const selectedCount = rows.filter((r) => r.selected).length;
  const warnCount = rows.filter((r) => r.warnings.length).length;
  // sama kutsu/band/moodi/maa tallentuu yhdeksi yhteydeksi
  const dupCount = useMemo(() => {
    const seen = new Set<string>();
    let n = 0;
    for (const r of rows) {
      if (!r.selected || r.band === null || !r.mode) continue;
      const key = `${r.callSign.toUpperCase()}|${roundBand(r.band)}|${r.mode.toUpperCase()}|${r.countryId}`;
      if (seen.has(key)) n++;
      else seen.add(key);
    }
    return n;
  }, [rows]);

  const isComplete = (r: ImportRow) => r.countryId !== null && r.band !== null && r.mode !== null;
  const update = (key: number, patch: Partial<ImportRow>) =>
    setRows((rs) =>
      rs.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, ...patch };
        // kun käyttäjä korjaa puuttuvan tiedon, rivi valitaan automaattisesti
        if (!('selected' in patch) && isComplete(next) && !rowFilter?.(next)) next.selected = true;
        return next;
      }),
    );
  const selectAll = (on: boolean) =>
    setRows((rs) => rs.map((r) => (visible.includes(r) ? { ...r, selected: on && isComplete(r) && !rowFilter?.(r) } : r)));

  const submit = async () => {
    const chosen = rows.filter((r) => r.selected && isComplete(r));
    setError(null);
    setProgress(0);
    let saved = 0;
    let added = 0;
    let rejected = 0;
    try {
      for (let i = 0; i < chosen.length; i += BATCH) {
        const batch = chosen.slice(i, i + BATCH).map((r) => ({
          callSign: r.callSign,
          date: r.date,
          time: r.time,
          band: r.band,
          mode: r.mode,
          countryId: r.countryId,
          frequency: r.frequency,
        }));
        const res = await api.post<QsoSaveResult>(target, { rows: batch });
        saved += res.saved;
        added += res.added;
        rejected += res.rejected;
        setProgress(Math.min(100, Math.round(((i + batch.length) / chosen.length) * 100)));
      }
      onDone({ saved, added, rejected });
    } catch (e) {
      setError(`${errorMessage(e)} (tallennettu ennen virhettä: ${saved})`);
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="stack">
      <div className="row">
        <div>
          <strong>{fileName}</strong>{' '}
          <span className="muted">
            · {rows.length.toLocaleString('fi-FI')} QSO:ta · {selectedCount.toLocaleString('fi-FI')} valittu
            {dupCount > 0 && (
              <span title="Sama kutsu, band, moodi ja maa tallentuvat yhdeksi yhteydeksi">
                {' '}
                · {dupCount.toLocaleString('fi-FI')} toistoa
              </span>
            )}
            {warnCount > 0 && <> · <span style={{ color: 'var(--warn)' }}>{warnCount} tarkistettavaa</span></>}
          </span>
        </div>
        <span className="spacer" />
        <div className="radio-group">
          {(
            [
              ['warnings', `Tarkistettavat (${warnCount})`],
              ['unselected', 'Valitsemattomat'],
              ['all', 'Kaikki'],
            ] as const
          ).map(([v, l]) => (
            <label key={v} className="check">
              <input
                type="radio"
                checked={view === v}
                onChange={() => {
                  setView(v);
                  setPage(1);
                }}
              />{' '}
              {l}
            </label>
          ))}
        </div>
      </div>

      <div className="table-wrap table-scroll">
        <table className="table compact">
          <thead>
            <tr>
              <th style={{ width: 1 }}>
                <input
                  type="checkbox"
                  aria-label="Valitse näkyvät"
                  checked={shown.length > 0 && shown.every((r) => r.selected)}
                  onChange={(e) => selectAll(e.target.checked)}
                />
              </th>
              <th>Kutsu</th>
              <th>Aika (UTC)</th>
              <th>Band</th>
              <th>Moodi</th>
              <th>Maa</th>
              <th>Huomio</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const blocked = !!rowFilter?.(r);
              return (
                <tr key={r.key} className={`${r.warnings.length ? 'warn' : ''} ${r.selected ? 'selected' : ''}`} style={blocked ? { opacity: 0.5 } : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Valitse ${r.callSign}`}
                      checked={r.selected}
                      disabled={!isComplete(r) || blocked}
                      onChange={(e) => update(r.key, { selected: e.target.checked })}
                    />
                  </td>
                  <td className="call">{r.callSign}</td>
                  <td className="mono nowrap">
                    {r.date} {r.time.slice(0, 5)}
                  </td>
                  <td>
                    {r.band !== null && !r.warnings.some((w) => w.includes('band')) ? (
                      <span className="mono">{formatBand(r.band)}</span>
                    ) : (
                      <select className="input-sm" value={r.band ?? ''} onChange={(e) => update(r.key, { band: e.target.value ? Number(e.target.value) : null })}>
                        <option value="">–</option>
                        {meta.data?.bands.map((b) => (
                          <option key={b.band} value={b.band}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td>
                    {r.mode && !r.warnings.some((w) => w.includes('moodi')) ? (
                      <span className="mono">{r.mode}</span>
                    ) : (
                      <select className="input-sm" value={r.mode ?? ''} onChange={(e) => update(r.key, { mode: e.target.value || null })}>
                        <option value="">{r.rawMode || '–'}</option>
                        {meta.data?.modes.map((m) => (
                          <option key={m.mode} value={m.mode}>
                            {m.mode}
                          </option>
                        ))}
                      </select>
                    )}
                  </td>
                  <td style={{ minWidth: 260 }}>
                    {r.warnings.some((w) => w.includes('maa')) ? (
                      <CountrySelect className="input-sm" value={r.countryId} invalid={r.countryId === null} onChange={(v) => update(r.key, { countryId: v })} />
                    ) : (
                      <span>
                        <span className="mono muted">{countries.get(r.countryId!)?.code}</span> {countries.get(r.countryId!)?.name}
                      </span>
                    )}
                  </td>
                  <td className="small" style={{ color: 'var(--warn)', whiteSpace: 'normal' }}>
                    {r.warnings.join(' · ')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!shown.length && <div className="empty">Ei rivejä tässä näkymässä.</div>}
      </div>

      <div className="row">
        <Pagination page={page} pages={pages} onPage={setPage} />
        <span className="spacer" />
        {progress !== null && (
          <div style={{ width: 200 }}>
            <div className="progress">
              <div style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
        <button className="btn btn-primary" disabled={!selectedCount || progress !== null} onClick={submit}>
          {progress !== null ? <Spinner /> : `Hyväksy ${selectedCount.toLocaleString('fi-FI')} QSO:ta`}
        </button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
    </div>
  );
}
